import React from "react";
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { AccountBalance as AccountBalanceIcon } from "@mui/icons-material";
import { LinkOff as LinkOffIcon } from "@mui/icons-material";
import { Debt } from "../../lib/types";
import { PlaidAccount } from "../../services/external/plaidService";

interface LinkAccountDialogProps {
  open: boolean;
  debtToLink: Debt | null;
  accounts: PlaidAccount[];
  onClose: () => void;
  onSelectAccount: (accountId: string | null) => void;
}

const LinkAccountDialog: React.FC<LinkAccountDialogProps> = ({
  open,
  debtToLink,
  accounts,
  onClose,
  onSelectAccount,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Account to "{debtToLink?.account}"</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select an account to link for automatic balance syncing, or unlink the
          current account.
        </Typography>
        <List>
          {debtToLink?.linkedAccountId && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => onSelectAccount(null)}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: "1px solid",
                  borderColor: "error.main",
                }}
              >
                <ListItemIcon>
                  <LinkOffIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Unlink Account"
                  secondary="Remove the current link"
                />
              </ListItemButton>
            </ListItem>
          )}
          {accounts.map((account) => (
            <ListItem key={account.account_id} disablePadding>
              <ListItemButton
                onClick={() => onSelectAccount(account.account_id)}
                selected={debtToLink?.linkedAccountId === account.account_id}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: "1px solid",
                  borderColor:
                    debtToLink?.linkedAccountId === account.account_id
                      ? "primary.main"
                      : "divider",
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: account.institution?.primary_color || "#1976d2",
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={account.name}
                  secondary={`${
                    account.institution?.name || "Unknown"
                  } •••${account.mask || ""} • ${
                    account.balances?.current != null
                      ? new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(account.balances.current)
                      : "N/A"
                  }`}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {accounts.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No accounts available. Connect a bank account first.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkAccountDialog;
